import React, { useMemo } from "react";
import CircleCheckTransparentIcon from "../../../icons/circle-check-transparent.icon";
import WarningTransparentCircleIcon from "../../../icons/warning-transparent-circle.icon";
import styles from "./index.module.scss";

export interface IOnboardingItem {
  id: string;
  label: string;
  status: "complete" | "in-progress" | "not-started" | "not-connected";
  children?: IOnboardingItem[];
}

export const getStatusIcon = (status: string) => {
  switch (status) {
    case "complete":
      return <CircleCheckTransparentIcon checked={true} width={16} height={16} className={styles.iconGreen} />
    case "in-progress":
      return <WarningTransparentCircleIcon width={15} height={15} className={styles.iconYellow} />;
    case "not-started":
      return <CircleCheckTransparentIcon checked={false} width={16} height={16} className={styles.iconGray} />;
    case "not-connected":
      return <CircleCheckTransparentIcon checked={false} width={16} height={16} className={styles.iconGray} />;
    default:
      return <CircleCheckTransparentIcon checked={false} width={16} height={16} className={styles.iconGray} />;
  }
};

export const getStatusText = (status: string) => {
  switch (status) {
    case "complete":
      return "Complete";
    case "in-progress":
      return "In Progress";
    case "not-started":
      return "Not Started";
    case "not-connected":
      return "Not Connected";
    default:
      return "Not Started";
  }
};

export const getStatusColorClass = (status: string) => {
  switch (status) {
    case "complete":
      return styles.complete;
    case "in-progress":
      return styles.inProgress;
    case "not-started":
      return styles.notStarted;
    case "not-connected":
      return styles.notConnected;
    default:
      return styles.notStarted;
  }
};

interface RenderStatusDisplayProps {
  item: IOnboardingItem;
}

const RenderStatusDisplay = ({ item }: RenderStatusDisplayProps) => {
  const useRelativeText = item.status === "complete" && (item.id === "wizard" || item.id === "proposal");
  const statusTextClass = useRelativeText ? styles.statusTextRelative : styles.statusText;
  const statusIcon = useMemo(() => getStatusIcon(item.status), [item.status]);
  const statusText = useMemo(() => getStatusText(item.status), [item.status]);
  const statusColorClass = useMemo(() => getStatusColorClass(item.status), [item.status]);

  return (
    <div className={styles.statusContainer}>
      <div className={styles.statusTextWithIcon}>{statusIcon}</div>
      <div className={styles.statusTextContainer}>
        <div className={`${statusTextClass} ${statusColorClass}`}>
          {statusText}
        </div>
      </div>
    </div>
  );
};

interface OnboardingItemProps {
  item: IOnboardingItem;
  isChild: boolean;
}

const OnboardingItem = ({ item, isChild }: OnboardingItemProps) => {
  return (
    <React.Fragment key={item.id}>
      <div className={`${styles.item} ${isChild ? styles.indented : ""}`}>
        <div className={styles.itemContent}>
          {item.id === "storefront" ? (
            <p className={styles.itemLabelParagraph}>{item.label}</p>
          ) : (
            <div className={styles.itemLabel}>{item.label}</div>
          )}
        </div>
        {<RenderStatusDisplay item={item} />}
      </div>
      {item.children?.map((child) => <OnboardingItem key={child.id} item={child} isChild={true} />)}
    </React.Fragment>
  );
};

export default OnboardingItem;